import { supabase } from '@/integrations/supabase/client';

// Start a new chat with a seller or return existing chat
export const startChatWithSeller = async (buyerId: string, sellerId: string, listingId: number): Promise<number | null> => {
  try {
    // First check if a chat already exists between these users for this listing
    const { data: existingChat, error: findError } = await supabase
      .from('chats')
      .select('id')
      .eq('buyer_id', buyerId)
      .eq('seller_id', sellerId)
      .eq('listing_id', listingId)
      .single();
    
    if (findError && findError.code !== 'PGRST116') {
      console.error('Error checking for existing chat:', findError);
      return null;
    }
    
    // If chat exists, return its ID
    if (existingChat) {
      return existingChat.id;
    }
    
    // Otherwise, create a new chat
    const { data: newChat, error: createError } = await supabase
      .from('chats')
      .insert({
        buyer_id: buyerId,
        seller_id: sellerId,
        listing_id: listingId
      })
      .select('id')
      .single();
    
    if (createError) {
      console.error('Error creating new chat:', createError);
      return null;
    }
    
    return newChat.id;
  } catch (error) {
    console.error('Error in startChatWithSeller:', error);
    return null;
  }
};

// Function to delete a message
export const deleteMessage = async (messageId: number): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId);
    
    if (error) {
      console.error('Error deleting message:', error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in deleteMessage:', error);
    return false;
  }
};

// Function to delete an entire chat
export const deleteChat = async (chatId: number): Promise<boolean> => {
  try {
    // First delete all messages in the chat
    const { error: messagesError } = await supabase
      .from('messages')
      .delete()
      .eq('chat_id', chatId);
    
    if (messagesError) {
      console.error('Error deleting chat messages:', messagesError);
      return false;
    }
    
    // Then delete the chat itself
    const { error: chatError } = await supabase
      .from('chats')
      .delete()
      .eq('id', chatId);
    
    if (chatError) {
      console.error('Error deleting chat:', chatError);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Error in deleteChat:', error);
    return false;
  }
};

// New function to mark messages as read
export const markMessagesAsRead = async (messageIds: number[]): Promise<boolean> => {
  if (!messageIds.length) return true;
  
  try {
    const { error } = await supabase
      .from('messages')
      .update({ is_read: true })
      .in('id', messageIds);
    
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error marking messages as read:', error);
    return false;
  }
};

// Get unread message count for a user
export const getUnreadMessageCount = async (userId: string): Promise<number> => {
  try {
    // Get all chats where the user is either buyer or seller
    const { data: chats, error: chatsError } = await supabase
      .from('chats')
      .select('id')
      .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`);
    
    if (chatsError) throw chatsError;
    
    if (!chats || chats.length === 0) {
      return 0;
    }
    
    // Get all unread messages in those chats where the user is not the sender
    const chatIds = chats.map(chat => chat.id);
    const { count, error: messagesError } = await supabase
      .from('messages')
      .select('id', { count: 'exact', head: true })
      .in('chat_id', chatIds)
      .eq('is_read', false)
      .neq('sender_id', userId);
    
    if (messagesError) throw messagesError;
    
    return count || 0;
  } catch (error) {
    console.error('Error fetching unread message count:', error);
    return 0;
  }
};
